import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const GlassButton = ({ children, variant = 'primary', icon, loading, disabled, onClick, fullWidth, className = '' }) => {
  const base = `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${fullWidth ? 'w-full' : ''}`;
  const variants = {
    primary: 'glassmorphic-button text-white hover:scale-[1.02]',
    outline: 'bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.08]',
    danger: 'border border-red-500/30 text-red-400 hover:bg-red-500/10',
    dangerFilled: 'text-white hover:scale-[1.02]',
  };
  return (
    <button
      onClick={onClick} disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={variant === 'dangerFilled' ? { background: 'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(220,38,38,0.3))', border: '1px solid rgba(239,68,68,0.4)', boxShadow: '0 4px 16px rgba(239,68,68,0.15)' } : {}}
    >
      {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
};

const DangerZoneSection = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [exportDataChecked, setExportDataChecked] = useState(false);
  const [understandConsequences, setUnderstandConsequences] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    setTimeout(() => { setIsExporting(false); alert('Your data export has been initiated. You will receive a download link via email within 24 hours.'); }, 2000);
  };

  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE' || !understandConsequences) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Failed to delete account');
      if (data?.error) throw new Error(data.error);

      await supabase.auth.signOut({ scope: 'local' });
      navigate('/login-registration');
      toast.success('Your account has been permanently deleted.');
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearCache = () => { if (window.confirm('Are you sure you want to clear all cached data?')) alert('Cache cleared successfully!'); };
  const handleResetSettings = () => { if (window.confirm('Are you sure you want to reset all settings to default?')) alert('Settings reset to defaults!'); };

  const GlassToggleCheck = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className="relative flex-shrink-0">
        <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
        <div className={`w-5 h-5 rounded-md transition-all duration-300 flex items-center justify-center ${checked ? '' : 'bg-white/10 border border-white/20'}`}
          style={checked ? { background: 'linear-gradient(135deg, rgba(254,131,56,0.6), rgba(236,103,30,0.5))', border: '1px solid rgba(254,131,56,0.5)', boxShadow: '0 0 8px rgba(254,131,56,0.3)' } : {}}>
          {checked && <Icon name="Check" size={12} className="text-white" />}
        </div>
      </div>
      <span className="text-sm text-white/70 group-hover:text-white transition-colors">{label}</span>
    </label>
  );

  return (
    <div className="space-y-6">
      {/* Danger Zone */}
      <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(239, 68, 68, 0.03)', border: '1px solid rgba(239, 68, 68, 0.15)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-start gap-3 mb-5">
          <Icon name="AlertTriangle" size={22} className="text-red-400 mt-0.5" />
          <div>
            <h2 className="text-xl font-bold text-red-400 mb-1">Danger Zone</h2>
            <p className="text-sm text-white/40">Irreversible actions that will permanently affect your account</p>
          </div>
        </div>

        <div className="p-5 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
          <h3 className="text-sm font-semibold text-white mb-2">Delete Account</h3>
          <p className="text-xs text-white/40 mb-4">Permanently delete your account and all associated data. This action cannot be undone.</p>

          <div className="mb-4">
            <p className="text-xs font-medium text-red-400 mb-2">This will permanently delete:</p>
            <ul className="text-xs text-white/40 space-y-1.5">
              <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-red-400" />All your projects and renders</li>
              <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-red-400" />Your subscription and billing history</li>
              <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-red-400" />All account settings and preferences</li>
              <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-red-400" />Access to all premium features</li>
            </ul>
          </div>

          <GlassButton variant="dangerFilled" icon="Trash2" onClick={() => setShowDeleteModal(true)}>Delete My Account</GlassButton>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(8px)' }}>
          <div className="glassmorphic-card rounded-2xl max-w-md w-full p-6" style={{ border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                <Icon name="AlertTriangle" size={22} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">Delete Account</h3>
                <p className="text-xs text-white/40">This action is permanent and cannot be undone.</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <GlassToggleCheck label="I want to export my data before deletion" checked={exportDataChecked} onChange={(e) => setExportDataChecked(e?.target?.checked)} />
              <GlassToggleCheck label="I understand this action is permanent and cannot be undone" checked={understandConsequences} onChange={(e) => setUnderstandConsequences(e?.target?.checked)} />

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Type DELETE to confirm</label>
                <input
                  type="text" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e?.target?.value)} placeholder="DELETE"
                  className="w-full px-4 py-3 rounded-xl text-white placeholder:text-white/30 text-sm bg-white/[0.05] border border-red-500/20 focus:border-red-500/40 focus:outline-none"
                  style={{ backdropFilter: 'blur(10px)' }}
                />
                <p className="text-[10px] text-white/30 mt-1.5">Type DELETE in capital letters to confirm</p>
              </div>
            </div>

            {deleteError && (
              <p className="text-xs text-red-400 mb-4">{deleteError}</p>
            )}

            <div className="flex gap-3">
              <GlassButton variant="dangerFilled" loading={isDeleting} disabled={deleteConfirmation !== 'DELETE' || !understandConsequences} icon="Trash2" onClick={handleDeleteAccount} fullWidth>
                Delete Account Permanently
              </GlassButton>
              <GlassButton variant="outline" onClick={() => { setShowDeleteModal(false); setDeleteConfirmation(''); setExportDataChecked(false); setUnderstandConsequences(false); }} disabled={isDeleting} fullWidth>
                Cancel
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZoneSection;
